import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts";
import ApplicationCard from "../components/ApplicationCard";
import LoadingIndicator from "../components/LoadingIndicator";
import PageTitle from "../components/PageTitle";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "http://127.0.0.1:8000"
).replace(
  /\/+$/,
  ""
);

export default function MyApplications() {
  const { userId } = useParams();
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [applications, setApplications] = useState([]);
  const [headerName, setHeaderName] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [activeOpen, setActiveOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);

  const isAdminViewing = currentUser?.role === "admin" && userId;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = isAdminViewing
      ? `${API_BASE_URL}/api/admin/profile/${userId}`
      : `${API_BASE_URL}/api/profile`;
    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (isAdminViewing) {
          const firstName = res.data?.user?.firstName || "";
          const lastName = res.data?.user?.lastName || "";
          setHeaderName(`${firstName} ${lastName}`.trim());
        } else {
          setHeaderName("");
        }
        const apps = Array.isArray(res.data?.applications)
          ? res.data.applications
          : [];
        setApplications(apps);
      })
      .catch((error) => {
        console.error("Error loading applications:", error);
      })
      .finally(() => setLoading(false));
  }, [userId, isAdminViewing]);

  const parseDDMMYYYYToDate = (value, timeValue) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (!match) return null;
    const [, dd, mm, yyyy] = match;
    const [hh, min] = String(timeValue || "00:00").split(":");
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh || 0),
      Number(min || 0)
    );
  };

  const isPositionActive = (startDate, startTime, endDate, endTime, explicitFlag) => {
    if (typeof explicitFlag === "boolean") return explicitFlag;
    const now = new Date();
    const start = parseDDMMYYYYToDate(startDate, startTime);
    const end = parseDDMMYYYYToDate(endDate, endTime);
    if (start && end) return now >= start && now <= end;
    if (end) return now <= end;
    return false;
  };

  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      const dateA = a.submitDate || "";
      const dateB = b.submitDate || "";
      return dateB.localeCompare(dateA);
    });
  }, [applications]);

  const { activeApplications, completedApplications } = useMemo(() => {
    const active = [];
    const completed = [];
    sortedApplications.forEach((app) => {
      const endDate = app.positionEndDate || app.endDate || app.applicationEndDate || "";
      const endTime = app.positionEndTime || app.endTime || "";
      const startDate = app.positionStartDate || app.startDate || "";
      const startTime = app.positionStartTime || app.startTime || "";
      const isActive = isPositionActive(startDate, startTime, endDate, endTime, app.isActive);
      if (isActive) {
        active.push(app);
      } else {
        completed.push(app);
      }
    });
    return { activeApplications: active, completedApplications: completed };
  }, [sortedApplications]);

  useEffect(() => {
    setActiveOpen(activeApplications.length > 0);
    setCompletedOpen(completedApplications.length > 0);
  }, [activeApplications.length, completedApplications.length]);

  const toDDMMYYYY = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      const m = v.match(/^(\d{2})-(\d{2})-(\d{4})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      const d = new Date(v);
      if (!isNaN(d)) {
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      }
      return v;
    }
    if (v instanceof Date && !isNaN(v)) {
      const dd = String(v.getDate()).padStart(2, "0");
      const mm = String(v.getMonth() + 1).padStart(2, "0");
      const yyyy = v.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    return "—";
  };

  const toDDMMYYYYHHMM = (dateStr, timeStr) => {
    if (!dateStr) return "";
    const base = toDDMMYYYY(dateStr);
    const embeddedTime = typeof dateStr === "string" ? dateStr.match(/\b(\d{2}:\d{2})\b/)?.[1] : "";
    const rawTime = timeStr || embeddedTime || "00:00";
    const [hh, mm] = String(rawTime).split(":");
    const padded = `${String(hh || "0").padStart(2, "0")}:${String(mm || "0").padStart(2, "0")}`;
    return `${base} ${padded}`;
  };


  const handleDelete = async (applicationId) => {
    if (!applicationId || deletingId) return;
    const confirmed = window.confirm(t("myApplications.confirmDelete"));
    if (!confirmed) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    setDeletingId(applicationId);
    try {
      await axios.delete(`${API_BASE_URL}/api/applications/${applicationId}/delete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications((prev) => prev.filter((app) => app.id !== applicationId));
    } catch (error) {
      console.error("Error deleting application:", error);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-y-5 pt-5">
        <LoadingIndicator size="sm" textClassName="mt-2 text-gray-600 dark:text-[var(--color-text-secondary)]" />
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-6 sm:pt-0 sm:pb-8">
        <PageTitle className="mb-6">
          {isAdminViewing ? (
            <>
              {t("myApplications.userApplications")} <span className="text-lg font-semibold">{headerName}</span>
            </>
          ) : (
            t("myApplications.title")
          )}
        </PageTitle>

        {sortedApplications.length === 0 ? (
          <div className="bg-white dark:bg-[var(--color-bg-card)] rounded-lg border border-gray-200 dark:border-[var(--color-border)] shadow-sm p-6 text-center">
            <p className="text-gray-700 dark:text-[var(--color-text-secondary)] font-semibold">{t("myApplications.none")}</p>
            <p className="text-sm text-gray-500 dark:text-[var(--color-text-muted)] mt-2">
              {t("myApplications.noneHint")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5">
            <div className="bg-white dark:bg-[var(--color-bg-card)] border border-gray-200 dark:border-[var(--color-border)] rounded-lg shadow-sm">
              <button
                type="button"
                onClick={() => setActiveOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-patras-buccaneer dark:text-[var(--color-text-secondary)] font-semibold"
              >
                <span>{t("myApplications.active")}</span>
                <span className="text-lg">{activeOpen ? "▼" : "\u25B6\uFE0E"}</span>
              </button>
              {activeOpen && (
                <div className="px-4 pb-4 grid grid-cols-1 gap-3">
                  {activeApplications.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-[var(--color-text-muted)]">{t("myApplications.noActive")}</div>
                  ) : (
                    activeApplications.map((app) => (
                      <ApplicationCard
                        key={app.id}
                        app={app}
                        toDDMMYYYYHHMM={toDDMMYYYYHHMM}
                      />
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-[var(--color-bg-card)] border border-gray-200 dark:border-[var(--color-border)] rounded-lg shadow-sm">
              <button
                type="button"
                onClick={() => setCompletedOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-patras-buccaneer dark:text-[var(--color-text-secondary)] font-semibold"
              >
                <span>{t("myApplications.completed")}</span>
                <span className="text-lg">{completedOpen ? "▼" : "\u25B6\uFE0E"}</span>
              </button>
              {completedOpen && (
                <div className="px-4 pb-4 grid grid-cols-1 gap-3">
                  {completedApplications.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-[var(--color-text-muted)]">{t("myApplications.noCompleted")}</div>
                  ) : (
                    completedApplications.map((app) => (
                      <ApplicationCard
                        key={app.id}
                        app={app}
                        toDDMMYYYYHHMM={toDDMMYYYYHHMM}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
